import { Box, Typography, useTheme } from "@mui/material";
import { FC, useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";

// Components
import ModalBase from "../../../../components/ModalBase";
import StatusChecker from "../../../../components/StatusChecker";
import CustomButton from "../../../../components/CustomButton";
import CustomTextField from "../../../../components/CustomTextField";

// Services
import { WebsiteManager } from "../../../../services/api/WebsiteManager";
import { ExternalApiManager } from "../../../../services/api/ExternalApiManager";
import { showFlashMessageWithTimeout } from "../../../../services/actions/flashMessage-actions";

// Styles & Types
import { useStyles } from "../AddApiKeyModal/addApiKeyModal.style";
import { ApiKeyModalProps } from "../AddApiKeyModal/types";

export const TrustPilotApiKeyModal: FC<ApiKeyModalProps> = ({
  isOpen,
  onClose,
  createdWebId,
  websiteData,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [trustPilotData, setTrustPilotData] = useState({
    businessUnitId: websiteData?.trustpilotBusinessUnitId ?? "",
  });

  const [status, setStatus] = useState({
    isLoading: false,
    isCheckLoading: false,
    isVerified: Boolean(websiteData?.trustpilotBusinessUnitId),
  });

  useEffect(() => {
    setTrustPilotData({
      businessUnitId: websiteData?.trustpilotBusinessUnitId ?? "",
    });
  }, [isOpen, websiteData?.trustpilotBusinessUnitId]);

  // Handle input changes for other fields
  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    field: keyof typeof trustPilotData,
  ) => {
    setTrustPilotData({
      ...trustPilotData,
      [field]: event.target.value,
    });
  };

  // Verify TrustPilot API credentials
  const verifyCredentials = async () => {
    const { businessUnitId } = trustPilotData;

    if (!businessUnitId) {
      showFlashMessageWithTimeout(
        dispatch,
        t("popups.fillApiAndBusinessUnitId"),
        "info",
      );
      return;
    }

    try {
      setStatus({ ...status, isCheckLoading: true });

      const externalApiManager = ExternalApiManager.getManager();
      const result = await externalApiManager.verifyTrustPilotApi(
        businessUnitId,
      );

      const isVerified = result.isVerified;

      setStatus({
        ...status,
        isCheckLoading: false,
        isVerified,
      });

      showFlashMessageWithTimeout(
        dispatch,
        isVerified
          ? t("popups.apiKeyVerificatedSuccess")
          : t("popups.verificationFailed"),
        isVerified ? "success" : "error",
      );
    } catch (error: any) {
      setStatus({ ...status, isCheckLoading: false });
      showFlashMessageWithTimeout(dispatch, error.toString(), "error");
    }
  };

  // Save TrustPilot configuration
  const saveTrustPilotConfig = async () => {
    const { businessUnitId } = trustPilotData;
    const { isVerified } = status;

    if (!createdWebId || !businessUnitId || !isVerified) {
      showFlashMessageWithTimeout(dispatch, t("popups.fillAllFields"), "info");
      return;
    }

    try {
      setStatus({ ...status, isLoading: true });

      const websiteManager = WebsiteManager.getManager();
      const result = await websiteManager.updateTrustPilotAsync(
        createdWebId,
        businessUnitId,
      );

      if (result) {
        showFlashMessageWithTimeout(dispatch, t("popups.added"), "success");
      }

      setStatus({ ...status, isLoading: false });

      onClose();
    } catch (error: any) {
      setStatus({ ...status, isLoading: false });
      showFlashMessageWithTimeout(dispatch, error.toString(), "error");
    }
  };

  // Render instructions list item
  const renderInstructionItem = (number: string, translationKey: string) => (
    <Box className={classes.rowWithMargin}>
      <Typography className={classes.listItemNumber}>{number}</Typography>
      <Typography className={classes.listItemText}>
        {t(translationKey)}
      </Typography>
    </Box>
  );

  return (
    <ModalBase isOpen={isOpen} onClose={onClose} center>
      <Box className={classes.body}>
        {/* Header with close button */}
        <Box className={classes.iconContainer}>
          <CloseIcon
            style={{ color: theme.palette.natural.t2, cursor: "pointer" }}
            onClick={onClose}
          />
        </Box>

        {/* Title */}
        <Typography className={classes.title}>
          {t("campaign.notifications.settings.displayRules.trustPilot.title")}
        </Typography>

        {/* Description */}
        <Box style={{ marginLeft: 32, marginRight: 32 }}>
          <Typography mb={2}>
            {t(
              "campaign.notifications.settings.displayRules.trustPilot.description",
            )}
          </Typography>
        </Box>

        {/* Form inputs */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1,
          }}
          className={classes.inputsContainer}
        >
          <Typography variant='body2' color='textSecondary'>
            https://trustpilot.com/review/
          </Typography>
          <CustomTextField
            fullWidth
            label={t("trustPilot.sourceUrlAddress") ?? ""}
            isRequired
            value={trustPilotData.businessUnitId}
            onChange={e => handleInputChange(e, "businessUnitId")}
          />
        </Box>

        <Box style={{ marginLeft: 32, marginRight: 32, marginTop: 16 }}>
          {/* Instructions */}
          <Typography className={classes.listTitle}>
            {t(
              "campaign.notifications.settings.displayRules.trustPilot.howToAddApiKey.title",
            )}
          </Typography>

          {renderInstructionItem(
            "1.",
            "campaign.notifications.settings.displayRules.trustPilot.howToAddApiKey.first",
          )}

          {renderInstructionItem(
            "2.",
            "campaign.notifications.settings.displayRules.trustPilot.howToAddApiKey.second",
          )}

          {renderInstructionItem(
            "3.",
            "campaign.notifications.settings.displayRules.trustPilot.howToAddApiKey.third",
          )}

          {/* Verification status */}
          <StatusChecker
            isActive={status.isVerified}
            isLoading={status.isCheckLoading}
            onClick={verifyCredentials}
          />

          {/* Action buttons */}
          <Box className={classes.buttonContainer}>
            <CustomButton
              isLoading={status.isLoading}
              title={
                websiteData?.trustpilotBusinessUnitId || status.isVerified
                  ? t("saveChanges")
                  : t("add")
              }
              onClick={saveTrustPilotConfig}
              isDisabled={!status.isVerified}
              style={{ width: 120, marginLeft: 20 }}
            />
            <CustomButton
              isLoading={false}
              title={t("close")}
              type='secondary'
              onClick={onClose}
              style={{ width: 120 }}
            />
          </Box>
        </Box>
      </Box>
    </ModalBase>
  );
};
